
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface BroadcastMessage {
  id: string;
  message: string;
  created_at: string;
}

export function useBroadcastNotifications() {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState<BroadcastMessage[]>([]);
  const [unread, setUnread] = useState<BroadcastMessage[]>([]);
  const [loading, setLoading] = useState(true);

  // Récupère tous les broadcast messages
  const fetchBroadcasts = useCallback(async () => {
    if (!user) {
      console.log('[BroadcastNotifications]: Pas d\'utilisateur connecté');
      setLoading(false);
      setBroadcasts([]);
      setUnread([]);
      return;
    }
    setLoading(true);

    try {
      // Fetch all broadcasts
      const { data, error } = await supabase
        .from('broadcast_messages')
        .select('id, message, created_at')
        .order('created_at', { ascending: false });

      console.log('[BroadcastNotifications]: Data broadcast_messages:', data, 'Erreur:', error);

      if (error) {
        console.error('[BroadcastNotifications]: Erreur:', error);
        setBroadcasts([]);
        setUnread([]);
        setLoading(false);
        return;
      }

      setBroadcasts(data || []);

      // Si pas de données, pas besoin d'aller plus loin
      if (!data || data.length === 0) {
        setUnread([]);
        setLoading(false);
        return;
      }

      try {
        // Fetch read broadcasts for this user
        const { data: readData, error: readError } = await supabase
          .from('broadcast_reads')
          .select('broadcast_id')
          .eq('user_id', user.id);

        console.log('[BroadcastNotifications]: Data broadcast_reads:', readData, 'Erreur:', readError);

        if (readError) {
          console.error('[BroadcastNotifications]: Erreur lecteurs:', readError);
          // En cas d'erreur, on suppose que rien n'a été lu
          setUnread(data);
          setLoading(false);
          return;
        }

        const readIds = (readData || []).map(r => r.broadcast_id);

        // Sont considérés non lus ceux que le user n'a pas en "broadcast_reads"
        const unreadList = (data || []).filter((msg: BroadcastMessage) => !readIds.includes(msg.id));
        setUnread(unreadList);

        console.log('[BroadcastNotifications]: Unread IDs:', unreadList.map(m => m.id));
      } catch (innerError) {
        console.error('[BroadcastNotifications]: Erreur interne:', innerError);
        // En cas d'erreur, on suppose que rien n'a été lu pour ne pas bloquer l'interface
        setUnread([]);
      }
    } catch (error) {
      console.error('[BroadcastNotifications]: Erreur globale:', error);
      setUnread([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBroadcasts();
  }, [user, fetchBroadcasts]);

  // Marque tous les broadcasts non lus comme "lus" pour ce user
  const markAllAsRead = async () => {
    if (!user || unread.length === 0) return;
    try {
      // Traiter les messages un par un pour mieux gérer les erreurs
      for (const msg of unread) {
        try {
          await supabase.from("broadcast_reads").insert({
            user_id: user.id,
            broadcast_id: msg.id
          }).select();
        } catch (err) {
          console.error(`Erreur lors du marquage du message ${msg.id} comme lu:`, err);
        }
      }
      await fetchBroadcasts();
    } catch (error) {
      console.error('[BroadcastNotifications]: Erreur de marquage comme lu:', error);
    }
  };

  return {
    broadcasts,
    unreadBroadcasts: unread,
    loading,
    markAllAsRead,
    refresh: fetchBroadcasts,
  };
}
